import { ref, watch, computed } from 'vue'
import { safeLocalStorage, isClient } from '@/composables/useSSR'
import { clearAttribution, disableGtag, persistAttribution } from '@/composables/useGoogleAds'

const STORAGE_KEY = 'cookieConsent'

export type ConsentChoice = 'accepted' | 'declined' | null

interface ConsentState {
  choice: ConsentChoice
  timestamp: number | null
}

const defaultState: ConsentState = {
  choice: null,
  timestamp: null
}

const loadState = (): ConsentState => {
  const storage = safeLocalStorage()
  if (!storage) return defaultState

  const stored = storage.getItem(STORAGE_KEY)
  if (stored) {
    try {
      const parsed = JSON.parse(stored)
      return { ...defaultState, ...parsed }
    } catch {
      return defaultState
    }
  }
  return defaultState
}

const saveState = (state: ConsentState): void => {
  const storage = safeLocalStorage()
  if (storage) {
    storage.setItem(STORAGE_KEY, JSON.stringify(state))
  }
}

// Shared reactive state
const consentState = ref<ConsentState>(isClient ? loadState() : defaultState)

// Sync to localStorage (client only)
if (isClient) {
  watch(
    consentState,
    (newValue) => {
      saveState(newValue)
    },
    { deep: true }
  )
}

// Listen for storage changes from other tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY && event.newValue) {
      try {
        consentState.value = { ...defaultState, ...JSON.parse(event.newValue) }
      } catch {
        // Ignore parse errors
      }
    }
  })
}

export function useCookieConsent() {
  const hasConsented = computed(() => consentState.value.choice !== null)
  const hasAcceptedMarketing = computed(() => consentState.value.choice === 'accepted')
  const showBanner = computed(() => !hasConsented.value)

  const acceptAll = () => {
    consentState.value = {
      choice: 'accepted',
      timestamp: Date.now()
    }
    // Campaign parameters were held in memory until this point (§ 25 TDDDG).
    persistAttribution()
  }

  /**
   * Declining must also undo anything a *previous* acceptance left behind —
   * this is reachable from the footer on a device that accepted earlier.
   */
  const declineMarketing = () => {
    consentState.value = {
      choice: 'declined',
      timestamp: Date.now()
    }
    clearAttribution()
    disableGtag()
  }

  const resetConsent = () => {
    consentState.value = { ...defaultState }
    const storage = safeLocalStorage()
    if (storage) {
      storage.removeItem(STORAGE_KEY)
    }
    clearAttribution()
    disableGtag()
  }

  return {
    consentState: computed(() => consentState.value),
    hasConsented,
    hasAcceptedMarketing,
    showBanner,
    acceptAll,
    declineMarketing,
    resetConsent
  }
}
